/**
 * **Personal data, as the package reads it** (W23, FR-48 – FR-53, ADR-0017,
 * plan D66 – D69; contract C-W23 of W23's prompts file).
 *
 * Every stored version is scanned for six kinds of an individual's personal
 * data; a reading scans the text it read too. What the service keeps — and so
 * all it ever answers a person — is **where a value is and what kind it is,
 * never the value**: a finding is its kind, its **masked form** (the last four
 * characters, `•••• •••• 1234`; an e-mail its first character and its domain),
 * its location and its source. **The masking is the server's**, not a display
 * trick here: every shape below carries the masked form as the service sent it.
 *
 * A value is shown in full only after a **reveal** — one value, asked for by a
 * person holding `personal_data:can_view`, logged by the service before it
 * answers (FR-52). The package keeps a revealed value in a component's state
 * and nowhere else: never in browser storage, never in a module, never in the
 * HTTP cache (the request says `no-store`).
 *
 * Nothing here talks to the network or imports the kit: `prodoc-client.ts`
 * reads through these, and `personal-data-panel.tsx` adds how a person sees
 * them.
 */

/* --------------------------------------------------------------- the kinds */

/**
 * The six kinds (FR-49), **most sensitive first** — the order a row names them
 * in, and the order `kinds` arrives in.
 */
export type PersonalDataKind = 'aadhaar' | 'pan' | 'card' | 'bank_account' | 'mobile' | 'email'

export const PERSONAL_DATA_KINDS: readonly PersonalDataKind[] = ['aadhaar', 'pan', 'card', 'bank_account', 'mobile', 'email']

/**
 * Where a version's scan stands (FR-50): `pending` until its scan ran,
 * `none_found`, `found`, or `not_scanned` with the reason — an image, a PDF with
 * no trustworthy text layer, an encrypted one, one over the limits.
 */
export type PersonalDataState = 'pending' | 'none_found' | 'found' | 'not_scanned'

export const PERSONAL_DATA_STATES: readonly PersonalDataState[] = ['pending', 'none_found', 'found', 'not_scanned']

/** Why a version was not scanned (C-E15's `reason`). A reading fills the gap: it scans the text it read, OCR included. */
export type NotScannedReason = 'image' | 'no_text_layer' | 'encrypted' | 'too_large' | 'unsupported'

/**
 * `document_out.personal_data` (C-W23) — the **current version's**: its state,
 * the reason it was not scanned, and the kinds found, the most sensitive first.
 */
export interface DocumentPersonalData {
  state: PersonalDataState
  reason: NotScannedReason | string | null
  /** The kinds found, Aadhaar → PAN → card → bank account → mobile → e-mail; empty unless `found`. */
  kinds: PersonalDataKind[]
}

/** The register's `personal_data=` (C-W23): a kind found, or `none` found, `not_scanned`, or `pending` — still checking. */
export type PersonalDataFilter = PersonalDataKind | 'none' | 'not_scanned' | 'pending'

export const PERSONAL_DATA_FILTERS: readonly PersonalDataFilter[] = [...PERSONAL_DATA_KINDS, 'none', 'not_scanned', 'pending']

/* ------------------------------------------------------------ a finding */

/**
 * Where a finding is (C-E15): a page and a box (integers, hundredths of a
 * point, from the top left — Evidence's units), a sheet and a cell, a JSON
 * pointer, or a text file's line with its characters.
 */
export type PersonalDataLocation =
  | { page: number; box: readonly [number, number, number, number] | null }
  | { sheet: string | null; cell: string }
  | { pointer: string }
  | { line: number; start: number | null; end: number | null }

/** Who found it: the version's own scan, or a reading of it (OCR included). */
export type FindingSource = 'scan' | 'parse'

/** One finding of `GET /documents/{id}/personal-data` (C-W23). The masked form only — never the value. */
export interface PersonalDataFinding {
  id: string
  kind: PersonalDataKind | string
  /** The server's masked form: the last four kept, every other letter or digit `•`. */
  masked: string
  location: PersonalDataLocation | null
  source: FindingSource | string | null
  created_at: string | null
}

/** The findings of a document's current version, and the answer's `meta`. */
export interface PersonalDataFindings {
  items: PersonalDataFinding[]
  state: PersonalDataState | null
  reason: string | null
  /** The version they were found in — the current one. */
  version_id: string | null
  /** `meta.can_reveal`: the service's word on whether this caller may reveal; `null` when unsaid. */
  can_reveal: boolean | null
}

/** A field's value, revealed (`POST …/extractions/{eid}/reveal`): what the field holds, for the moment it is shown. */
export interface RevealedField {
  field_path: string
  value: unknown
}

/* ---------------------------------------------------------- normalisers */

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
const str = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null)
const int = (value: unknown): number | null => (typeof value === 'number' && Number.isInteger(value) ? value : null)

const isKind = (value: unknown): value is PersonalDataKind => typeof value === 'string' && (PERSONAL_DATA_KINDS as readonly string[]).includes(value)

/** The kinds the package knows, once each, the most sensitive first — whatever order they came in. */
export function kindsOf(raw: unknown): PersonalDataKind[] {
  const given = Array.isArray(raw) ? raw.filter(isKind) : []
  return PERSONAL_DATA_KINDS.filter((k) => given.includes(k))
}

/**
 * `personal_data` as the service sent it — `null` when it sent none (a service
 * older than W23) or a state the package does not know, so a screen shows
 * nothing rather than a guess.
 */
export function personalDataOf(raw: unknown): DocumentPersonalData | null {
  const r = asRecord(raw)
  const state = str(r?.state)
  if (!r || !state || !(PERSONAL_DATA_STATES as readonly string[]).includes(state)) return null
  return { state: state as PersonalDataState, reason: str(r.reason), kinds: kindsOf(r.kinds) }
}

/** A finding's location in one of C-E15's four shapes, or `null`. */
export function locationOf(raw: unknown): PersonalDataLocation | null {
  const r = asRecord(raw)
  if (!r) return null
  const page = int(r.page)
  if (page !== null && page >= 1) {
    const box = Array.isArray(r.box) && r.box.length === 4 && r.box.every((n) => typeof n === 'number' && Number.isFinite(n)) ? (r.box as unknown as [number, number, number, number]) : null
    return { page, box }
  }
  const cell = str(r.cell)
  if (cell) return { sheet: str(r.sheet), cell }
  // RFC 6901: empty (the whole file) or starting with `/`.
  if (typeof r.pointer === 'string' && (r.pointer === '' || r.pointer.startsWith('/'))) return { pointer: r.pointer }
  const line = int(r.line)
  if (line !== null && line >= 1) return { line, start: int(r.start), end: int(r.end) }
  return null
}

export function findingOf(raw: unknown): PersonalDataFinding | null {
  const r = asRecord(raw)
  const id = str(r?.id)
  const masked = str(r?.masked)
  // A row with no masked form is shown as nothing — never as whatever else it carried.
  if (!r || !id || !masked) return null
  return { id, kind: str(r.kind) ?? 'unknown', masked, location: locationOf(r.location), source: str(r.source), created_at: str(r.created_at) }
}

/** The list's `data` and `meta` (C-W23), read defensively. */
export function findingsOf(body: unknown, meta: Record<string, unknown> = {}): PersonalDataFindings {
  const items = (Array.isArray(body) ? body : []).map(findingOf).filter((f): f is PersonalDataFinding => f !== null)
  const state = str(meta.state)
  return {
    items,
    state: state && (PERSONAL_DATA_STATES as readonly string[]).includes(state) ? (state as PersonalDataState) : null,
    reason: str(meta.reason),
    version_id: str(meta.version_id),
    can_reveal: typeof meta.can_reveal === 'boolean' ? meta.can_reveal : null,
  }
}

/**
 * A finding's value from a reveal's `data: {value}` — text as it is, a number as
 * its digits; anything else is no value at all (`null`), never a guess.
 */
export function revealedValueOf(body: unknown): string | null {
  const value = asRecord(body)?.value
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return null
}

export function revealedFieldOf(body: unknown, fieldPath: string): RevealedField {
  const r = asRecord(body) ?? {}
  return { field_path: str(r.field_path) ?? fieldPath, value: 'value' in r ? r.value : null }
}

/**
 * `meta.masked_paths` of a person's extraction read (C-W23): the field paths
 * whose values — and their evidence's `span` — the service masked. Concrete
 * paths (`employee.pan`, `transactions[3].account`) as the service answers;
 * `[]` or `[*]` in a path is read as any row.
 */
export function maskedPathsOf(raw: unknown): string[] {
  return Array.isArray(raw) ? [...new Set(raw.filter((p): p is string => typeof p === 'string' && p.length > 0))] : []
}

/** Whether `path` is one of `masked` — exactly, or by a `[]` / `[*]` row wildcard. */
export function isMaskedPath(masked: readonly string[], path: string): boolean {
  for (const m of masked) {
    if (m === path) return true
    if (m.includes('[]') || m.includes('[*]')) {
      // Every character literal, then each escaped `[]` or `[*]` widened to any row's index.
      const pattern = m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\\\[(?:\\\*)?\\\]/g, '\\[[0-9]+\\]')
      if (new RegExp(`^${pattern}$`).test(path)) return true
    }
  }
  return false
}

/** The mask's character (the service's `masking.MASK`): a text still holding one is a masked form, not a value. */
export const MASK_CHAR = '•'

export const holdsMask = (text: string): boolean => text.includes(MASK_CHAR)

/* -------------------------------------------------------------- words */

/** A kind as a label — a badge, a filter's choice, a list's line. */
export const PERSONAL_DATA_KIND_WORDS: Record<PersonalDataKind, string> = {
  aadhaar: 'Aadhaar',
  pan: 'PAN',
  card: 'Card number',
  bank_account: 'Bank account',
  mobile: 'Mobile number',
  email: 'E-mail address',
}

/** The same inside a sentence: *also contains PAN, a mobile number and an e-mail address*. */
const KIND_IN_A_SENTENCE: Record<PersonalDataKind, string> = {
  aadhaar: 'an Aadhaar number',
  pan: 'a PAN',
  card: 'a card number',
  bank_account: 'a bank account',
  mobile: 'a mobile number',
  email: 'an e-mail address',
}

const humanise = (code: string): string => {
  const words = code.replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : code
}

export const personalDataKindLabel = (kind: string): string => (isKind(kind) ? PERSONAL_DATA_KIND_WORDS[kind] : humanise(kind))

/** "a, b and c". */
function listed(words: string[]): string {
  if (words.length <= 1) return words.join('')
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

/** The ⓘ's words for the kinds after the first (FR-51): *Also contains a PAN and a mobile number.* Empty for one kind or none. */
export function alsoContainsText(kinds: readonly PersonalDataKind[]): string {
  const rest = kinds.slice(1)
  return rest.length ? `Also contains ${listed(rest.map((k) => KIND_IN_A_SENTENCE[k]))}.` : ''
}

/** The column's words for a state that is not `found` (FR-51). */
export const PERSONAL_DATA_STATE_WORDS: Record<PersonalDataState, string> = {
  pending: 'Checking',
  none_found: 'None found',
  found: 'Found',
  not_scanned: 'Not scanned',
}

/** The register filter's choices (C-W23) in words. */
export const PERSONAL_DATA_FILTER_WORDS: Record<PersonalDataFilter, string> = {
  ...PERSONAL_DATA_KIND_WORDS,
  none: 'None found',
  not_scanned: 'Not scanned',
  pending: 'Checking',
}

/** What a row's cell says, as text — the export's, and a state's or the first kind's word. `''` for no answer. */
export function personalDataText(data: DocumentPersonalData | null | undefined, all = false): string {
  if (!data) return ''
  if (data.state !== 'found') return PERSONAL_DATA_STATE_WORDS[data.state]
  if (data.kinds.length === 0) return PERSONAL_DATA_STATE_WORDS.found
  return all ? data.kinds.map((k) => PERSONAL_DATA_KIND_WORDS[k]).join(', ') : PERSONAL_DATA_KIND_WORDS[data.kinds[0]!]
}

/** `not_scanned`'s reason in a few words, under the column's *Not scanned*. */
const NOT_SCANNED_SHORT: Record<NotScannedReason, string> = {
  image: 'An image',
  no_text_layer: 'No text layer',
  encrypted: 'Encrypted',
  too_large: 'Too large',
  unsupported: 'Not a file the scan reads',
}

export const notScannedShort = (reason: string | null | undefined): string => (reason ? (NOT_SCANNED_SHORT[reason as NotScannedReason] ?? humanise(reason)) : '')

/** `not_scanned`'s reason as the panel says it — and what fills the gap. */
const NOT_SCANNED_TEXT: Record<NotScannedReason, string> = {
  image: 'It is an image, and the scan reads text only — it does no OCR.',
  no_text_layer: 'It is a scan with no text layer ProDoc can trust, and the scan does no OCR.',
  encrypted: 'It is encrypted, and the scan holds no password.',
  too_large: 'It is larger than the scan reads.',
  unsupported: 'It is not a kind of file the scan reads.',
}

export const notScannedText = (reason: string | null | undefined): string =>
  reason ? (NOT_SCANNED_TEXT[reason as NotScannedReason] ?? `${humanise(reason)}.`) : 'The scan could not read its text.'

/** Where a finding is, said as a person looks for it: *Page 2*, *Sheet1, cell B3*, *JSON /partA/pan*, *Line 17*. */
export function locationText(location: PersonalDataLocation | null | undefined): string {
  if (!location) return 'Location not recorded'
  if ('page' in location) return `Page ${location.page}`
  if ('cell' in location) return location.sheet ? `${location.sheet}, cell ${location.cell}` : `Cell ${location.cell}`
  if ('pointer' in location) return location.pointer === '' ? 'The whole file' : `JSON ${location.pointer}`
  return `Line ${location.line}`
}

/** Who found it. */
export const findingSourceText = (source: string | null | undefined): string =>
  source === 'scan' ? 'Found by the scan' : source === 'parse' ? 'Found when it was read' : source ? humanise(source) : ''
